<template>
  <div class="modal fade" @keyup.esc.prevent="closeModal(false)" tabindex="-1" id="bannerModal" aria-labelledby="bannerModalLabel">
    <div class="modal-dialog modal-xl">
      <div v-if="loaded" class="modal-content shadow-none">
        <loader />
      </div>
      <div v-else class="modal-content shadow-none">
        <div class="modal-header p-5">
          <h5 class="modal-title">{{ message.id ? 'Редактирование баннера' : 'Новый баннер' }}</h5>
          <div @click="closeModal(false)" class="btn btn-icon btn-sm btn-active-light-primary ms-2">
            <i class="ki-duotone ki-cross fs-2x"><span class="path1"></span><span class="path2"></span></i>
          </div>
        </div>
        <div class="modal-body p-5">
          <div class="row">
            <div class="col-md-6">
              <div class="custom-select dynamic mb-5">
                <label for="message.campaignId" class="required">Кампания</label>
                <v-select
                  v-model="message.campaignId"
                  input-id="message.campaignId"
                  :options="campaigns"
                  :reduce="(item) => item.id"
                  label="name"
                  placeholder="Выберите..."
                  class="mb-3 bg-gray-100 form rounded"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="custom-select dynamic mb-5">
                <label for="message.linkType" class="required">Тип ссылки</label>
                <v-select
                  v-model="message.linkType"
                  input-id="message.linkType"
                  :options="dictLinkTypes"
                  :reduce="(item) => item.code"
                  label="label"
                  placeholder="Выберите..."
                  class="mb-3 bg-gray-100 form rounded"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-10">
              <input-field v-model="message.link" id="message.link" name="message.link" placeholder="Ссылка" type="text" mb="5" />
            </div>
            <div class="col-md-2 d-flex align-items-center mb-5">
              <div class="form-check form-check-custom form-check-solid">
                <input id="message.btnCloseShow" v-model="message.btnCloseShow" class="form-check-input" type="checkbox">
                <label class="form-check-label ms-2" for="message.btnCloseShow">Кнопка закрытия</label>
              </div>
            </div>
          </div>

          <div class="fs-6 fw-bold mb-3">Локализации</div>
          <div v-for="(loc, index) in message.localizations" :key="loc.locale" class="border rounded p-4 mb-4">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <span class="fw-bold text-gray-700">{{ localeLabel(loc.locale) }}</span>
              <div class="form-check form-check-custom form-check-solid">
                <input
                  :id="`default-${loc.locale}`"
                  v-model="defaultLocale"
                  class="form-check-input"
                  type="radio"
                  :value="loc.locale"
                >
                <label class="form-check-label ms-2" :for="`default-${loc.locale}`">По умолчанию</label>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <input-field
                  v-model="message.localizations[index].title"
                  :id="`loc.${loc.locale}.title`"
                  :name="`loc.${loc.locale}.title`"
                  placeholder="Заголовок"
                  type="text"
                  mb="5"
                />
              </div>
              <div class="col-md-6">
                <input-field
                  v-model="message.localizations[index].btnLabel"
                  :id="`loc.${loc.locale}.btnLabel`"
                  :name="`loc.${loc.locale}.btnLabel`"
                  placeholder="Текст кнопки"
                  type="text"
                  mb="5"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <input-field
                  v-model="message.localizations[index].content"
                  :id="`loc.${loc.locale}.content`"
                  :name="`loc.${loc.locale}.content`"
                  placeholder="Текст"
                  type="text"
                  mb="5"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <input-field
                  v-model="message.localizations[index].imageUrl"
                  :id="`loc.${loc.locale}.imageUrl`"
                  :name="`loc.${loc.locale}.imageUrl`"
                  placeholder="URL изображения"
                  type="text"
                  mb="0"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer p-5 justify-content-between">
          <div>
            <button type="button" @click="closeModal(false)" class="btn btn-light btn-sm">Закрыть</button>
            <button
              v-if="message.id"
              type="button"
              @click="deleteMessage"
              :disabled="deleting"
              class="btn btn-danger btn-sm ms-2"
            >
              {{ deleting ? 'Удаление...' : 'Удалить' }}
            </button>
          </div>
          <button type="button" @click="saveMessage" :disabled="saving" class="btn btn-primary btn-sm">
            {{ saving ? 'Сохранение...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader'
import InputField from '@/components/Fields/InputField'
import vSelect from 'vue-select'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'

const API_BASE = '/energo-inapp-messaging/api/admin/v1'

const DEFAULT_LOCALES = ['ru', 'uz', 'en']

const LOCALE_LABELS = {
  ru: 'Русский (ru)',
  uz: 'Узбекский (uz)',
  en: 'Английский (en)',
}

const createDefaultLocalization = (locale, isDefault = false) => ({
  locale,
  title: '',
  content: '',
  imageUrl: '',
  btnLabel: '',
  isDefault,
})

const createDefaultLocalizations = () => DEFAULT_LOCALES.map((locale, index) => (
  createDefaultLocalization(locale, index === 0)
))

const createDefaultMessage = () => ({
  id: null,
  campaignId: null,
  linkType: null,
  link: '',
  btnCloseShow: true,
  localizations: createDefaultLocalizations(),
})

export default {
  name: 'BannerModal',
  components: { Loader, InputField, vSelect },
  props: {
    itemData: {
      type: Object,
      default: null,
    },
    dictLinkTypes: {
      type: Array,
      default: () => [],
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loaded: false,
      saving: false,
      deleting: false,
      message: createDefaultMessage(),
      defaultLocale: 'ru',
    };
  },
  mounted() {
    this.hydrateMessage();
    setTimeout(() => {
      const modalEl = document.getElementById('bannerModal');
      if (modalEl) {
        modalEl.addEventListener('mousedown', (event) => {
          if (event.target.classList.contains('modal')) this.closeModal(false);
        });
      }
    }, 10);
  },
  methods: {
    localeLabel(locale) {
      return LOCALE_LABELS[locale] ?? locale;
    },
    hydrateMessage() {
      const source = this.itemData || {};
      const sourceLocs = Array.isArray(source.localizations) ? source.localizations : [];
      const localizations = DEFAULT_LOCALES.map((locale) => {
        const existing = sourceLocs.find((l) => l.locale === locale);
        return existing
          ? { ...createDefaultLocalization(locale), ...existing }
          : createDefaultLocalization(locale);
      });
      const defaultLoc = localizations.find((l) => l.isDefault);
      this.defaultLocale = defaultLoc?.locale ?? 'ru';
      this.message = {
        ...createDefaultMessage(),
        ...source,
        localizations,
      };
    },
    closeModal(isUpdate) {
      const modalInstance = bootstrap.Modal.getInstance(document.getElementById('bannerModal'));
      if (modalInstance) modalInstance.hide();
      this.$emit('closeModal', isUpdate);
    },
    buildPayload() {
      return {
        id: this.message.id || undefined,
        campaignId: this.message.campaignId,
        linkType: this.message.linkType,
        link: String(this.message.link || '').trim(),
        btnCloseShow: Boolean(this.message.btnCloseShow),
        localizations: this.message.localizations.map((loc) => ({
          locale: loc.locale,
          title: loc.title ? String(loc.title).trim() : '',
          content: loc.content ? String(loc.content).trim() : '',
          imageUrl: loc.imageUrl ? String(loc.imageUrl).trim() : '',
          btnLabel: loc.btnLabel ? String(loc.btnLabel).trim() : '',
          isDefault: loc.locale === this.defaultLocale,
        })),
      };
    },
    validate() {
      if (!this.message.campaignId) {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Выберите кампанию' });
        return false;
      }
      if (this.message.linkType == null || this.message.linkType === '') {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Выберите тип ссылки' });
        return false;
      }
      if (!this.message.link || !String(this.message.link).trim()) {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Заполните ссылку' });
        return false;
      }
      const hasTitle = this.message.localizations.some((loc) => loc.title && String(loc.title).trim());
      if (!hasTitle) {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Заполните заголовок хотя бы для одной локали' });
        return false;
      }
      return true;
    },
    saveMessage() {
      if (!this.validate()) return;

      if (this.message.id) {
        notify({
          duration: 7000,
          type: 'warn',
          speed: 1000,
          title: 'Внимание',
          text: 'Обновление сообщений на бэкенде пока не применяется — изменения могут не сохраниться',
        });
      }

      this.saving = true;
      const payload = this.buildPayload();
      const request = this.message.id
        ? axios.put(`${API_BASE}/message`, payload)
        : axios.post(`${API_BASE}/message`, payload);

      request
        .then(() => {
          notify({
            duration: 7000,
            type: 'success',
            speed: 1000,
            title: 'Успешно',
            text: this.message.id ? 'Запрос на обновление отправлен' : 'Баннер создан',
          });
          this.closeModal(true);
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: err?.response?.data?.errorDescription ?? err.message,
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    deleteMessage() {
      if (!this.message.id) return;
      if (!window.confirm('Удалить баннер?')) return;

      this.deleting = true;
      axios.delete(`${API_BASE}/message/${this.message.id}`)
        .then(() => {
          notify({
            duration: 7000,
            type: 'warn',
            speed: 1000,
            title: 'Внимание',
            text: 'Удаление на бэкенде может быть не реализовано',
          });
          this.closeModal(true);
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: err?.response?.data?.errorDescription ?? err.message,
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
}
</script>

<style scoped>

</style>
